import React, { useState, useEffect } from 'react';
import { races, homelands, professions } from './data'; // Assumed data update
import { estimateProbability, getDifficulty, getMaxStats, formatOdds, formatChance } from './tools';

import AppPage from '../common/AppPage';
import { Link } from 'react-router-dom';

const StatsCalc = () => {
  // Updated state names to reflect 'race' and 'profession'
  const [selectedRace, setSelectedRace] = useState(races[0]);
  const [selectedHomeland, setSelectedHomeland] = useState(homelands[races[0]][0].name);
  const [selectedProfession, setSelectedProfession] = useState(professions[races[0]][0].name);
  const [currentStatsum, setCurrentStatsum] = useState(80);
  const [str, setStr] = useState(0);
  const [int, setInt] = useState(0);
  const [wil, setWil] = useState(0);
  const [dex, setDex] = useState(0);
  const [con, setCon] = useState(0);
  const [targetStatsum, setTargetStatsum] = useState(0);
  const [countRerolls, setCountRerolls] = useState(0);
  const [chance, setChance] = useState(0);
  const [characterStats, setCharacterStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const workerRef = React.useRef(null);

  const homeland = homelands[selectedRace]?.find(h => h.name === selectedHomeland);
  const profession = professions[selectedRace]?.find(p => p.name === selectedProfession);
  const maxStats = getMaxStats(homeland, profession);
  const difficulty = getDifficulty(homeland, profession);

  // setup worker
  useEffect(() => {
    setupWorker();
    return () => workerRef.current.terminate();
  }, []);

  const setupWorker = () => {
    workerRef.current = new Worker(new URL('./worker.js', import.meta.url), { type: 'module' });
    workerRef.current.onmessage = (e) => {
      const { type, payload } = e.data;
      if (type === 'run-result') {
        setBusy(false);
        setCountRerolls(payload.count);
        setChance(payload.chance);
        setCharacterStats(payload.stats);
      }
    };
  }

  const handleSelectedRaceChange = (newRace) =>{
    setSelectedRace(newRace);
    setSelectedHomeland(homelands[newRace][0].name);
    setSelectedProfession(professions[newRace][0].name);
  };

  const rerollForCount = (count) => {
    if (count > 100) {
      setCountRerolls(0);
      setChance(0);
      setCharacterStats(null);
      setBusy(true);
    }

    workerRef.current.postMessage({ type: 'run-count', payload: { homeland, profession, currentStatsum, count }});
  };

  const rerollForTarget = () => {
    setCountRerolls(0);
    setChance(0);
    setCharacterStats(null);
    setBusy(true);

    workerRef.current.postMessage({ type: 'run-target', payload: { homeland, profession, currentStatsum, str, int, wil, dex, con, targetStatsum }});
  };

  const rerollForTargetOdds = (count) => {
    setCountRerolls(0);
    setChance(0);
    setCharacterStats(null);
    setBusy(true);

    workerRef.current.postMessage({ type: 'run-target-odds', payload: { homeland, profession, currentStatsum, str, int, wil, dex, con, targetStatsum, count }});
  };

  const estimateTargetOdds = () => {
    const mathChance = estimateProbability(homeland, profession, currentStatsum, { str, int, wil, dex, con, targetStatsum });
    alert(`${formatChance(mathChance)}% chance per reroll    ( 1 : ${formatOdds(mathChance)} odds )`);
  };

  const cancelWorker = () => {
    workerRef.current.terminate();
    setupWorker();

    setBusy(false);
    setCountRerolls(0);
    setChance(0);
    setCharacterStats(null);
  };

  return (
    <AppPage pageClass="stats-page">
      <div>
        <div className="d-flex align-items-center mb-3">
          <h5 className="mb-0">WoTMud Stats / Reroll Simulator</h5>
          <Link className="ms-auto small text-muted text-decoration-none" to="/oldstats">Old Stats DB &gt;</Link>
        </div>

        <div className="row gx-3">
          <div className="col-md-6 mb-3">
          <div className="card">
            <div className="card-header small py-1">Current</div>
              <div className="card-body">
              <div className="row gx-2">
                
                {/* Race Selection */}
                <div className="col-auto mb-3">
                  <label className="small">Race:</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedRace}
                    onChange={(e) => handleSelectedRaceChange(e.target.value)}>
                    {races.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>

                {/* Class Selection */}
                <div className="col-auto mb-3">
                  <label className="small">Class:</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedProfession}
                    onChange={(e) => setSelectedProfession(e.target.value)}>
                    {professions[selectedRace].map(p => (
                      <option key={p.name} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Homeland Selection */}
                <div className="col-auto mb-3">
                  <label className="small">Homeland:</label>
                  <select 
                    className="form-select form-select-sm"
                    value={selectedHomeland} 
                    onChange={(e) => setSelectedHomeland(e.target.value)}>
                    {homelands[selectedRace].map(h => (
                      <option key={h.name} value={h.name}>{h.name}</option>
                    ))}
                  </select>
                </div>

                {/* Current Statsum */}
                <div className="col-auto mb-3">
                  <label className="small">Statsum:</label>
                  <select className="form-select form-select-sm" value={currentStatsum} onChange={(e) => setCurrentStatsum(Number(e.target.value))}>
                    <option value={60}>60</option>
                    <option value={61}>61</option>
                    <option value={62}>62</option>
                    <option value={63}>63</option>
                    <option value={64}>64</option>
                    <option value={65}>65</option>
                    <option value={66}>66</option>
                    <option value={67}>67</option>
                    <option value={68}>68</option>
                    <option value={69}>69</option>
                    <option value={70}>70</option>
                    <option value={71}>71</option>
                    <option value={72}>72</option>
                    <option value={73}>73</option>
                    <option value={74}>74</option>
                    <option value={75}>75</option>
                    <option value={76}>76</option>
                    <option value={77}>77</option>
                    <option value={78}>78</option>
                    <option value={79}>79</option>
                    <option value={80}>80</option>
                    <option value={81}>81</option>
                    <option value={82}>82</option>
                    <option value={83}>83</option>
                    <option value={84}>84</option>
                    <option value={85}>85</option>
                    <option value={86}>86</option>
                    <option value={87}>87</option>
                    <option value={88}>88</option>
                    <option value={89}>89</option>
                    <option value={90}>90</option>
                    <option value={91}>91</option>
                    <option value={92}>92</option>
                    <option value={93}>93</option>
                    <option value={94}>94</option>
                    <option value={95}>95</option>
                    <option value={96}>96</option>
                    <option value={97}>97</option>
                    <option value={98}>98</option>
                    <option value={99}>99</option>
                  </select>
                </div>

                <div className="col-auto mb-3 gx-4">
                  <label className="small">Max Stats:</label>
                  <div className="small py-1">{maxStats.str} {maxStats.int} {maxStats.wil} {maxStats.dex} {maxStats.con} {maxStats.statsum} {difficulty}</div>
                </div>
              </div>

              <div className="row gx-2">
                <div className="col-auto align-self-end mb-3">
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => rerollForCount(1)}>
                    Reroll 1
                  </button>
                </div>

                <div className="col-auto align-self-end mb-3">
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => rerollForCount(10)}>
                    Reroll 10
                  </button>
                </div>

                <div className="col-auto align-self-end mb-3">
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => rerollForCount(100)}>
                    Reroll 100
                  </button>
                </div>

                <div className="col-auto align-self-end mb-3">
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => rerollForCount(1000)}>
                    Reroll 1,000
                  </button>
                </div>

                <div className="col-auto align-self-end mb-3">
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => rerollForCount(10000)}>
                    Reroll 10,000
                  </button>
                </div>

                <div className="col-auto align-self-end mb-3">
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => rerollForCount(100000)}>
                    Reroll 100,000
                  </button>
                </div>
              </div>
            </div>
            </div>
          </div>

          <div className="col-md-6 mb-3">
          <div className="card">
            <div className="card-header small py-1">Target</div>
            <div className="card-body">
            <div className="row gx-2">
              <div className="col-auto mb-3">
                <label className="small">Str:</label>
                <select className="form-select form-select-sm" value={str} onChange={(e) => setStr(Number(e.target.value))}>
                  <option value="0">Any</option>
                  <option value={9}>9</option>
                  <option value={10}>10</option>
                  <option value={11}>11</option>
                  <option value={12}>12</option>
                  <option value={13}>13</option>
                  <option value={14}>14</option>
                  <option value={15}>15</option>
                  <option value={16}>16</option>
                  <option value={17}>17</option>
                  <option value={18}>18</option>
                  <option value={19}>19</option>
                  <option value={20}>20</option>
                  <option value={21}>21</option>
                </select>
              </div>

              <div className="col-auto mb-3">
                <label className="small">Int:</label>
                <select className="form-select form-select-sm" value={int} onChange={(e) => setInt(Number(e.target.value))}>
                  <option value="0">Any</option>
                  <option value={5}>5</option>
                  <option value={6}>6</option>
                  <option value={7}>7</option>
                  <option value={8}>8</option>
                  <option value={9}>9</option>
                  <option value={10}>10</option>
                  <option value={11}>11</option>
                  <option value={12}>12</option>
                  <option value={13}>13</option>
                  <option value={14}>14</option>
                  <option value={15}>15</option>
                  <option value={16}>16</option>
                  <option value={17}>17</option>
                  <option value={18}>18</option>
                  <option value={19}>19</option>
                </select>
              </div>

              <div className="col-auto mb-3">
                <label className="small">Wil:</label>
                <select className="form-select form-select-sm" value={wil} onChange={(e) => setWil(Number(e.target.value))}>
                  <option value="0">Any</option>
                  <option value={5}>5</option>
                  <option value={6}>6</option>
                  <option value={7}>7</option>
                  <option value={8}>8</option>
                  <option value={9}>9</option>
                  <option value={10}>10</option>
                  <option value={11}>11</option>
                  <option value={12}>12</option>
                  <option value={13}>13</option>
                  <option value={14}>14</option>
                  <option value={15}>15</option>
                  <option value={16}>16</option>
                  <option value={17}>17</option>
                  <option value={18}>18</option>
                  <option value={19}>19</option>
                </select>
              </div>

              <div className="col-auto mb-3">
                <label className="small">Dex:</label>
                <select className="form-select form-select-sm" value={dex} onChange={(e) => setDex(Number(e.target.value))}>
                  <option value="0">Any</option>
                  <option value={9}>9</option>
                  <option value={10}>10</option>
                  <option value={11}>11</option>
                  <option value={12}>12</option>
                  <option value={13}>13</option>
                  <option value={14}>14</option>
                  <option value={15}>15</option>
                  <option value={16}>16</option>
                  <option value={17}>17</option>
                  <option value={18}>18</option>
                  <option value={19}>19</option>
                </select>
              </div>

              <div className="col-auto mb-3">
                <label className="small">Con:</label>
                <select className="form-select form-select-sm" value={con} onChange={(e) => setCon(Number(e.target.value))}>
                  <option value="0">Any</option>
                  <option value={9}>9</option>
                  <option value={10}>10</option>
                  <option value={11}>11</option>
                  <option value={12}>12</option>
                  <option value={13}>13</option>
                  <option value={14}>14</option>
                  <option value={15}>15</option>
                  <option value={16}>16</option>
                  <option value={17}>17</option>
                  <option value={18}>18</option>
                  <option value={19}>19</option>
                </select>
              </div>

              <div className="col-auto mb-3">
                <label className="small">Statsum:</label>
                <select className="form-select form-select-sm" value={targetStatsum} onChange={(e) => setTargetStatsum(Number(e.target.value))}>
                  <option value="0">Any</option>
                  <option value={70}>70</option>
                  <option value={71}>71</option>
                  <option value={72}>72</option>
                  <option value={73}>73</option>
                  <option value={74}>74</option>
                  <option value={75}>75</option>
                  <option value={76}>76</option>
                  <option value={77}>77</option>
                  <option value={78}>78</option>
                  <option value={79}>79</option>
                  <option value={80}>80</option>
                  <option value={81}>81</option>
                  <option value={82}>82</option>
                  <option value={83}>83</option>
                  <option value={84}>84</option>
                  <option value={85}>85</option>
                  <option value={86}>86</option>
                  <option value={87}>87</option>
                  <option value={88}>88</option>
                  <option value={89}>89</option>
                  <option value={90}>90</option>
                  <option value={91}>91</option>
                  <option value={92}>92</option>
                </select>
              </div>
            </div>
            <div className="row gx-2">
              <div className="col-auto align-self-end mb-3">
                <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => rerollForTarget()}>
                  Reroll to Target
                </button>
              </div>

              <div className="col-auto align-self-end mb-3">
                <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => rerollForTargetOdds(100000)}>
                 Simulated Odds
                </button>
              </div>

              <div className="col-auto align-self-end mb-3">
                <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => estimateTargetOdds()}>
                  Mathematic Odds
                </button>
              </div>
            </div>
            </div>
            </div>
          </div>

          {characterStats && (
            <div>
              <div className="card bg-transparent border-secondary" style={{minHeight: '200px'}}>
                <div className="card-header small py-1">

                  {countRerolls === 1 && (
                    <>Results after 1 reroll</>
                  )}

                  {countRerolls > 1 && (
                    <>Best results after {countRerolls.toLocaleString()} rerolls</>
                  )}

                  {chance && (
                    <>,
                      <span className="ms-3">{formatChance(chance)}% chance per reroll</span>
                      <span className="ms-3"><span className="text-muted"></span>( 1 : {formatOdds(chance)} odds )</span>
                    </>
                  )}
                </div>
              <div className="card-body">
                {characterStats.map((char, index) => (
                  <div key={index}>
                    Str: {char.str} Int: {char.int} Wil: {char.wil} Dex: {char.dex} Con: {char.con} Sum: {char.statsum}
                  </div>
                ))}

                {characterStats.length === 0 && (
                  <div>No results.</div>
                )}
              </div>
              </div>
            </div>
          )}

          {busy && (
            <div className="px-3">
              <div className="small my-1">Running...  This could take awhile.</div>
              <div className="my-2">
                  <button className="btn btn-secondary btn-sm" onClick={() => cancelWorker()}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppPage>
  );
};

export default StatsCalc;